import React from 'react';
import type { SessionUser } from '@lumos/contracts';
import {
  ContinueIcon,
  FavoritesIcon,
  HomeIcon,
  LiveIcon,
  LumosMark,
  MoviesIcon,
  SettingsIcon,
  TvIcon,
} from './icons.js';
import type { NavKey } from './navigation.js';

const NAV_ITEMS: ReadonlyArray<{
  key: NavKey;
  label: string;
  href: string;
  Icon: React.ComponentType<{ className?: string }>;
}> = [
  { key: 'home', label: 'Главная', href: '#/', Icon: HomeIcon },
  { key: 'movies', label: 'Фильмы', href: '#/movies', Icon: MoviesIcon },
  { key: 'tv', label: 'Сериалы', href: '#/tv', Icon: TvIcon },
  { key: 'livetv', label: 'ТВ', href: '#/livetv', Icon: LiveIcon },
  { key: 'favorites', label: 'Избранное', href: '#/favorites', Icon: FavoritesIcon },
  { key: 'continue', label: 'История', href: '#/continue', Icon: ContinueIcon },
];
// «Настройки» в этом списке нет намеренно: своих настроек у зрителя не бывает —
// и общие, и индивидуальные задаёт администратор в админке. Пункт, ведущий на
// пустой экран, обещал бы обратное.

/**
 * Боковое меню (десктоп) и нижняя навигация (телефон) вокруг всех зрительских
 * экранов.
 *
 * На широком экране меню всегда раскрыто, с подписями: значки без слов
 * заставляли угадывать, а места справа хватает с запасом. На планшете меню
 * свёрнуто в значки (подпись во всплывающей) и парит над левым полем страницы
 * без своей панели: содержимое стоит от плашки пункта на том же шаге, что
 * плашка от края экрана (правило и токены — styles.css, `--menu-gap`). На
 * телефоне — полоса внизу.
 *
 * Подсветка выбранного раздела — ОДНА на всё меню и переезжает к новому пункту
 * сдвигом, а не гаснет на старом и загорается на новом: глаз следит за
 * движением и сразу видит, куда перешли. Сдвиг — трансформацией, без пересчёта
 * раскладки.
 */
export function AppShell({
  active,
  children,
  brandName = 'Lumos',
  user = null,
  onLogout,
}: {
  active: NavKey;
  children: React.ReactNode;
  /** Название из настроек установки (server-driven). По умолчанию — Lumos. */
  brandName?: string;
  /** Текущий пользователь (или `null` — не вошёл): для футера входа/выхода. */
  user?: SessionUser | null;
  onLogout?: () => void;
}): React.JSX.Element {
  const activeIndex = NAV_ITEMS.findIndex((item) => item.key === active);
  const indicator = { '--nav-index': Math.max(0, activeIndex) } as React.CSSProperties;

  return (
    <div className="app-shell">
      <nav className="sidebar" aria-label="Основная навигация">
        <a className="sidebar-brand" href="#/" aria-label={`${brandName} — на главную`} title={brandName}>
          <LumosMark className="sidebar-brand-mark" />
          <span className="sidebar-brand-text">{brandName}</span>
        </a>
        <div className="sidebar-nav" style={indicator}>
          {activeIndex >= 0 && <span className="sidebar-indicator" aria-hidden="true" />}
          <ul className="sidebar-list">
            {NAV_ITEMS.map(({ key, label, href, Icon }) => (
              <li key={key}>
                <a
                  className="sidebar-link"
                  href={href}
                  aria-current={active === key ? 'page' : undefined}
                  title={label}
                >
                  <Icon className="sidebar-icon" />
                  <span className="sidebar-label">{label}</span>
                </a>
              </li>
            ))}
          </ul>
        </div>

        <div className="sidebar-foot">
          {/* Ссылка на Admin Panel (журнал, логи, пользователи, устройства) —
              только администратору; иначе туда попасть можно лишь по прямому URL. */}
          {user?.role === 'admin' && (
            <a className="sidebar-link" href="#/admin" title="Админка">
              <SettingsIcon className="sidebar-icon" />
              <span className="sidebar-label">Админка</span>
            </a>
          )}

          {/* Вход/выход внизу меню: без него в dev нельзя залогиниться админом. */}
          {user ? (
            <button type="button" className="sidebar-link sidebar-auth-btn" onClick={onLogout} title="Выйти">
              <span className="sidebar-avatar" aria-hidden="true">
                {user.displayName.slice(0, 1).toUpperCase()}
              </span>
              <span className="sidebar-label sidebar-user">
                {user.displayName}
                <small>Выйти</small>
              </span>
            </button>
          ) : (
            <a className="sidebar-link" href="#/login" title="Войти">
              <span className="sidebar-avatar" aria-hidden="true">
                ↪
              </span>
              <span className="sidebar-label">Войти</span>
            </a>
          )}
        </div>
      </nav>

      <div className="app-content">{children}</div>

      <nav className="bottom-nav" aria-label="Основная навигация" style={indicator}>
        {activeIndex >= 0 && <span className="bottom-nav-indicator" aria-hidden="true" />}
        {NAV_ITEMS.map(({ key, label, href, Icon }) => (
          <a
            key={key}
            className="bottom-nav-link"
            href={href}
            aria-current={active === key ? 'page' : undefined}
            aria-label={label}
            title={label}
          >
            <Icon className="bottom-nav-icon" />
            <span className="bottom-nav-label">{label}</span>
          </a>
        ))}
      </nav>
    </div>
  );
}
